import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const PrePrimaryProgramSection = () => {
    const { school_slug } = useParams();

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* ---------------- SECTION STATE ---------------- */

    const [form, setForm] = useState({
        heading: "",
        introduction_text: "",
        highlight_text: "",
    });

    /* IMAGES */
    const [image, setImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    /* ---------------- IMAGE STATES (CSR STYLE) ---------------- */
    const handleFileChange = (e) => {
        //setImage(e.target.files[0]);
        const file = e.target.files[0];
        setImage(file);

        if (file) {
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    /* ------------- Block State ------------------*/

    const [blocks, setBlocks] = useState([
        {
            block_type: "bullet_list",
            heading: "",
            items: [""]
        },
        {
            block_type: "card_grid",
            heading: "",
            intro: "",
            items: [""]
        },
        {
            block_type: "paragraph",
            heading: "",
            content: ""
        }
    ]);

    /* ---------------- FETCH DATA ---------------- */
    useEffect(() => {
        fetchContent();
    }, [school_slug]);

    const fetchContent = async () => {
        try {
            const res = await axios.get(
                `${baseAPIUrl}admin/academic_page/get_pre_primary/${school_slug}`,
                { withCredentials: true }
            );

            if (res.data.status === 1 && res.data.data) {
                const { section, blocks } = res.data.data;

                /* ---------- SECTION ---------- */
                if (section) {
                    setForm({
                        heading: section.heading || "",
                        introduction_text: section.introduction_text || "",
                        highlight_text: section.highlight_text || "",
                    });

                    if (section.section_image) {
                        setPreviewImage(
                            `${baseAPIUrl}assets/images/academics/${section.section_image}?t=${Date.now()}`
                        );
                    }
                }


                /* ------------ BLOCKS --------------- */
                if (res.data.data.blocks && res.data.data.blocks.length) {
                    const fetchedBlocks = res.data.data.blocks;
                    setBlocks(prevBlocks =>
                        prevBlocks.map(block => {
                            const saved = fetchedBlocks.find(
                                b => b.block_type === block.block_type
                            );

                            if (!saved) return block;

                            // PARAGRAPH
                            if (block.block_type === "paragraph") {
                                return {
                                    ...block,
                                    heading: saved.heading || "",
                                    content: saved.content || ""
                                };
                            }

                            // BULLET LIST & CARD GRID
                            return {
                                ...block,
                                heading: saved.heading || "",
                                intro: saved.intro || "",
                                items: JSON.parse(saved.content || "[]")
                            };
                        })
                    );
                }


            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /* HANDLERS */

    const addListItem = (blockIndex) => {
        setBlocks(prev => {
            const updated = [...prev];
            updated[blockIndex].items.push("");
            return updated;
        });
    };

    const removeListItem = (blockIndex, itemIndex) => {
        setBlocks(prev => {
            const updated = [...prev];
            if (updated[blockIndex].items.length <= 1) return prev;
            updated[blockIndex].items.splice(itemIndex, 1);
            return updated;
        });
    };

    const addCard = (blockIndex) => {
        setBlocks(prev => {
            const updated = [...prev];
            if (updated[blockIndex].items.length >= 4) return prev;
            updated[blockIndex].items.push("");
            return updated;
        });
    };

    const removeCard = (blockIndex, cardIndex) => {
        setBlocks(prev => {
            const updated = [...prev];
            if (updated[blockIndex].items.length <= 1) return prev;
            updated[blockIndex].items.splice(cardIndex, 1);
            return updated;
        });
    };

    const updateBlockHeading = (index, value) => {
        const updated = [...blocks];
        updated[index].heading = value;
        setBlocks(updated);
    };

    const updateBlockIntro = (index, value) => {
        setBlocks(prev => {
            const updated = [...prev];
            updated[index].intro = value;
            return updated;
        });
    };

    const updateParagraph = (index, value) => {
        const updated = [...blocks];
        updated[index].content = value;
        setBlocks(updated);
    };

    const updateItem = (blockIndex, itemIndex, value) => {
        const updated = [...blocks];
        // safety check
        if (!Array.isArray(updated[blockIndex].items)) return;

        // directly replace string value
        updated[blockIndex].items[itemIndex] = value;
        setBlocks(updated);
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };


    /* ---------------- Submit ---------------- */
    const sanitizeBlocks = (blocks) => {
        return blocks.map(block => {
            // paragraph block
            if (block.block_type === "paragraph") {
                return {
                    block_type: "paragraph",
                    heading: block.heading || "",
                    content: block.content || ""
                };
            }

            // bullet_list & card_grid
            return {
                block_type: block.block_type,
                heading: block.heading || "",
                intro: block.intro || "",
                items: block.items.map(item => item ?? "")
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        try {
            setLoading(true);
            const cleanBlocks = sanitizeBlocks(blocks);
            const formData = new FormData();
            formData.append("school_slug", school_slug);
            formData.append("section", JSON.stringify(form));
            formData.append("blocks", JSON.stringify(cleanBlocks));
            if (image) {
                formData.append("section_image",image);
            }
            const res = await axios.post(
                `${baseAPIUrl}admin/academic_page/add_edit_pre_primary`,
                formData,
                { withCredentials: true }
            );
            await fetchContent();
            setMessage(res.data.message);
            scrollToTop();
        } catch (err) {
            console.error(err);
            setMessage("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <p>Loading...</p>;
    return (
        <div className="content-wrapper">
            {/* Content Header (Page header) */}
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 text-dark">Pre-primary-program section</h1>
                        </div>{/* /.col */}
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/admin/dashboard">Dashboard</a></li>
                                <li class="breadcrumb-item active">Academic page</li>
                                <li class="breadcrumb-item active">Pre-primary-program section</li>
                            </ol>
                        </div>{/* /.col */}
                    </div>{/* /.row */}
                </div>{/* /.container-fluid */}
            </div>
            {/* /.content-header */}
            {/* Main content */}
            <section className="content">
                <div className="container-fluid">
                    <div class="row">
                        <div class="col-12">
                            <div class="card">
                                {/*-- /.card-header --*/}
                                <div class="card-header">
                                    <h4>Edit Pre-primary-program section</h4>
                                </div>
                                <form onSubmit={handleSubmit} encType="multipart/form-data">
                                    <div class="card-body">
                                        {message && (
                                            <p className="text-center text-danger">{message}</p>
                                        )}
                                        {/* Heading */}
                                        <input type="hidden" name="school_slug" value={school_slug}></input>
                                        <div className="form-group">
                                            <label>Heading of section</label>
                                            <input
                                                type="text"
                                                name="heading"
                                                className="form-control"
                                                placeholder="Heading of section"
                                                value={form.heading}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* Introduction text */}
                                        <div className="form-group">
                                            <label>Introduction text</label>
                                            <textarea
                                                name="introduction_text"
                                                rows="5"
                                                className="form-control"
                                                placeholder="Introduction text"
                                                value={form.introduction_text}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* Blocks content */}
                                        <div className="form-group">
                                            {blocks.map((block, index) => (
                                                <div key={index} className="border p-3 mb-3">

                                                    <strong>{block.block_type.toUpperCase()}</strong>

                                                    {/* DIFFERENT UI PER BLOCK TYPE */}
                                                    {block.block_type === "bullet_list" && (
                                                        <>
                                                            <input
                                                                className="form-control mb-2"
                                                                placeholder="Section heading"
                                                                value={block.heading}
                                                                onChange={e => updateBlockHeading(index, e.target.value)}
                                                            />

                                                            {block.items.map((item, i) => (
                                                                <div key={i} className="d-flex gap-2 mb-2">
                                                                    <textarea
                                                                        className="form-control"
                                                                        placeholder={`List item ${i + 1}`}
                                                                        value={item}
                                                                        onChange={e =>
                                                                            updateItem(index, i, e.target.value)
                                                                        }
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger"
                                                                        onClick={() => removeListItem(index, i)}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ))}

                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                onClick={() => addListItem(index)}
                                                            >
                                                                + Add List Item
                                                            </button>
                                                        </>
                                                    )}

                                                    {block.block_type === "card_grid" && (
                                                        <>
                                                            <input
                                                                className="form-control mb-2"
                                                                placeholder="Section heading"
                                                                value={block.heading}
                                                                onChange={e => updateBlockHeading(index, e.target.value)}
                                                            />

                                                            <textarea
                                                                className="form-control mb-3"
                                                                placeholder="Intro text"
                                                                value={block.intro}
                                                                onChange={e =>
                                                                    updateBlockIntro(index, e.target.value)
                                                                }
                                                            />

                                                            {block.items.map((card, i) => (
                                                                <div key={i} className="d-flex gap-2 mb-2">
                                                                    <input
                                                                        className="form-control"
                                                                        placeholder={`Card ${i + 1}`}
                                                                        value={card}
                                                                        onChange={e =>
                                                                            updateItem(index, i, e.target.value)
                                                                        }
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger"
                                                                        onClick={() => removeCard(index, i)}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ))}

                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                disabled={block.items.length >= 4}
                                                                onClick={() => addCard(index)}
                                                            >
                                                                + Add Card
                                                            </button>
                                                        </>
                                                    )}

                                                    {block.block_type === "paragraph" && (
                                                        <>
                                                            <input
                                                                className="form-control mb-2"
                                                                placeholder="Section heading"
                                                                value={block.heading}
                                                                onChange={e => updateBlockHeading(index, e.target.value)}
                                                            />

                                                            <textarea
                                                                className="form-control"
                                                                rows="4"
                                                                placeholder="Paragraph content"
                                                                value={block.content}
                                                                onChange={e =>
                                                                    updateParagraph(index, e.target.value)
                                                                }
                                                            />
                                                        </>
                                                    )}


                                                </div>
                                            ))}

                                        </div>

                                        {/* Highlight text */}
                                        <div className="form-group">
                                            <label>Highlight text</label>
                                            <textarea
                                                name="highlight_text"
                                                rows="6"
                                                className="form-control"
                                                placeholder={`Highlight text`}
                                                value={form.highlight_text}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* image uploD */}
                                        <div className="form-group">
                                            <label>Section image</label>

                                            <div className="input-group">
                                                <div className="custom-file">
                                                    <input
                                                        type="file"
                                                        className="custom-file-input"
                                                        accept="image/*"
                                                        onChange={handleFileChange}
                                                    />
                                                    <label className="custom-file-label">
                                                        {image ? image.name : "Choose file"}
                                                    </label>
                                                </div>
                                                <div className="input-group-append">
                                                    <span className="input-group-text">Upload</span>
                                                </div>
                                            </div>

                                            {/* Existing Image Preview */}

                                            <div className="mt-2">
                                                <img
                                                    src={
                                                        previewImage
                                                    }
                                                    alt="Preview"
                                                    className="img-fluid img-bordered"
                                                    style={{ width: "100px" }}
                                                />
                                            </div>

                                        </div>

                                    </div>

                                    <div className="card-footer">
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? "Submitting..." : "Submit"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div >
    )
}
export default PrePrimaryProgramSection;